// Mirrors the backend's Shared/Constants/Permissions.cs PermissionCodes catalog exactly — the
// two are not auto-derived from each other, keep them in lockstep by hand.

export const PERMISSIONS = {
  DASHBOARD_VIEW: 'Dashboard.View',

  COMPANY_VIEW: 'Company.View',
  COMPANY_MANAGE: 'Company.Manage',

  WAREHOUSE_VIEW: 'Warehouse.View',
  WAREHOUSE_MANAGE: 'Warehouse.Manage',

  MATERIAL_VIEW: 'Material.View',
  MATERIAL_MANAGE: 'Material.Manage',

  BOM_VIEW: 'Bom.View',
  BOM_MANAGE: 'Bom.Manage',

  PRODUCTION_VIEW: 'Production.View',
  PRODUCTION_MANAGE: 'Production.Manage',
  PRODUCTION_EXECUTE: 'Production.Execute',
  JOB_WORK_VIEW: 'JobWork.View',
  JOB_WORK_MANAGE: 'JobWork.Manage',
  JOB_WORK_EXECUTE: 'JobWork.Execute',
  WEIGHT_PRODUCTION_VIEW: 'WeightProduction.View',
  WEIGHT_PRODUCTION_MANAGE: 'WeightProduction.Manage',
  WEIGHT_PRODUCTION_EXECUTE: 'WeightProduction.Execute',

  SUPPLIER_VIEW: 'Supplier.View',
  SUPPLIER_MANAGE: 'Supplier.Manage',

  CUSTOMER_VIEW: 'Customer.View',
  CUSTOMER_MANAGE: 'Customer.Manage',

  CLIENT_VIEW: 'Client.View',
  CLIENT_MANAGE: 'Client.Manage',

  PROCESS_CONFIGURATION_VIEW: 'ProcessConfiguration.View',
  PROCESS_CONFIGURATION_MANAGE: 'ProcessConfiguration.Manage',

  APPROVAL_MATRIX_VIEW: 'ApprovalMatrix.View',
  APPROVAL_MATRIX_MANAGE: 'ApprovalMatrix.Manage',
  APPROVAL_MATRIX_ACT: 'ApprovalMatrix.Act',

  USER_VIEW: 'User.View',
  USER_MANAGE: 'User.Manage',

  ROLE_VIEW: 'Role.View',
  ROLE_MANAGE: 'Role.Manage',

  PURCHASE_REQUEST_VIEW: 'PurchaseRequest.View',
  PURCHASE_REQUEST_MANAGE: 'PurchaseRequest.Manage',

  PURCHASE_ORDER_VIEW: 'PurchaseOrder.View',
  PURCHASE_ORDER_MANAGE: 'PurchaseOrder.Manage',

  GATE_ENTRY_VIEW: 'GateEntry.View',
  GATE_ENTRY_MANAGE: 'GateEntry.Manage',

  GRN_VIEW: 'Grn.View',
  GRN_MANAGE: 'Grn.Manage',

  QC_VIEW: 'Qc.View',
  QC_EXECUTE: 'Qc.Execute',

  QC_CHECKPOINT_VIEW: 'QcCheckpoint.View',
  QC_CHECKPOINT_MANAGE: 'QcCheckpoint.Manage',

  PUTAWAY_VIEW: 'Putaway.View',
  PUTAWAY_EXECUTE: 'Putaway.Execute',

  MATERIAL_REQUEST_VIEW: 'MaterialRequest.View',
  MATERIAL_REQUEST_MANAGE: 'MaterialRequest.Manage',
  MATERIAL_REQUEST_APPROVE: 'MaterialRequest.Approve',

  DISPATCH_ORDER_VIEW: 'DispatchOrder.View',
  DISPATCH_ORDER_MANAGE: 'DispatchOrder.Manage',

  PICKING_VIEW: 'Picking.View',
  PICKING_EXECUTE: 'Picking.Execute',

  PACKING_VIEW: 'Packing.View',
  PACKING_EXECUTE: 'Packing.Execute',

  LOADING_VIEW: 'Loading.View',
  LOADING_EXECUTE: 'Loading.Execute',

  GATE_EXIT_VIEW: 'GateExit.View',
  GATE_EXIT_EXECUTE: 'GateExit.Execute',

  STOCK_ADJUSTMENT_VIEW: 'StockAdjustment.View',
  STOCK_ADJUSTMENT_MANAGE: 'StockAdjustment.Manage',
  STOCK_ADJUSTMENT_APPROVE: 'StockAdjustment.Approve',

  STOCK_TRANSFER_VIEW: 'StockTransfer.View',
  STOCK_TRANSFER_MANAGE: 'StockTransfer.Manage',

  CYCLE_COUNT_VIEW: 'CycleCount.View',
  CYCLE_COUNT_EXECUTE: 'CycleCount.Execute',

  RETURN_NOTE_VIEW: 'ReturnNote.View',
  RETURN_NOTE_MANAGE: 'ReturnNote.Manage',

  REPORT_VIEW: 'Report.View',
  REPORT_EXPORT: 'Report.Export',

  AUDIT_LOG_VIEW: 'AuditLog.View',

  LABEL_TEMPLATE_VIEW: 'LabelTemplate.View',
  LABEL_TEMPLATE_MANAGE: 'LabelTemplate.Manage',

  SYSTEM_SETTING_VIEW: 'SystemSetting.View',
  SYSTEM_SETTING_MANAGE: 'SystemSetting.Manage',

  CLIENT_CONTRACT_VIEW: 'ClientContract.View',
  CLIENT_CONTRACT_MANAGE: 'ClientContract.Manage',

  BILLING_VIEW: 'Billing.View',
  BILLING_MANAGE: 'Billing.Manage',

  CLIENT_PORTAL_DASHBOARD_VIEW: 'ClientPortalDashboard.View',
  CLIENT_PORTAL_INVENTORY_VIEW: 'ClientPortalInventory.View',
  CLIENT_PORTAL_GRN_VIEW: 'ClientPortalGrn.View',
  CLIENT_PORTAL_DISPATCH_ORDER_VIEW: 'ClientPortalDispatchOrder.View',
  CLIENT_PORTAL_INVOICE_VIEW: 'ClientPortalInvoice.View',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ROLES = {
  // Platform-level role for creating/managing companies across tenants — see User.CompanyId (null
  // for a SuperAdmin) and SystemRoles.SuperAdmin on the backend.
  SUPER_ADMIN: 'SuperAdmin',
  ADMINISTRATOR: 'Administrator',
  WAREHOUSE_MANAGER: 'Warehouse Manager',
  OPERATOR: 'Operator',
  APPROVER: 'Approver',
  VIEWER: 'Viewer',
  // A Client's own read-only login into their slice of this 3PL company's data — see
  // SystemRoles.ClientPortal on the backend for why this can never be aliased to Administrator.
  CLIENT_PORTAL: 'Client Portal',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export interface UserDto {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  avatarColor: string;
  avatarUrl: string | null;
  // Null companyId/companyName means this is a SuperAdmin — a platform-level user not tied to any
  // one company.
  companyId: string | null;
  companyName: string | null;
  branchId: string | null;
  departmentName: string | null;
  branchName: string | null;
  // "Company" | "Branch" | "Warehouse" — how far this user can see within their company. The
  // warehouse below is their hard boundary on a "Warehouse" scope, a plain default otherwise.
  accessScope: 'Company' | 'Branch' | 'Warehouse';
  defaultWarehouseId: string | null;
  defaultWarehouseName: string | null;
  isActive: boolean;
  isLocked: boolean;
  lastLoginDate: string | null;
  roles: string[];
  permissions: string[];
  // Non-null only for a Client Portal user — the post-login redirect uses this to send them to
  // /portal/dashboard instead of /dashboard.
  clientId: string | null;
  clientName: string | null;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: UserDto;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export function getInitials(user: Pick<UserDto, 'firstName' | 'lastName'>): string {
  return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
}
